import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardMedia, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import Carousel from 'react-multi-carousel';
import moment from 'moment';
import 'moment/locale/es';
import 'react-multi-carousel/lib/styles.css';

const useStyle = makeStyles((theme) => ({
    root: {
        minWidth: '100%',
        backgroundColor: '#E0E0E0',
        marginRight: '20px',
        '&:hover': {
            cursor: 'pointer'
        }
    },
    title: {
        fontSize: 14
    }
}));

const TarjetaNotificacion = (props) => {
    const { notificacion, onSelect } = props;
    const classes = useStyle();

    const handleClick = () => {
        if (onSelect) {
            onSelect(notificacion);
        }
    };

    return (
        <Card className={classes.root} variant="outlined" onClick={handleClick}>
            <CardMedia
                component="img"
                height="360"
                image={
                    notificacion?.portadaCursoMostrar != null
                        ? notificacion?.portadaCursoMostrar
                        : 'https://cdn.pixabay.com/photo/2012/12/14/04/41/e-mail-69911_960_720.jpg'
                }
                alt="notificacion"
            />
            <CardContent>
                <Typography className={classes.title} color="textSecondary" gutterBottom>
                    Anuncio
                </Typography>
                <Typography variant="h5" component="h2">
                    {notificacion.anuncio || notificacion.Titulo}
                </Typography>
                {notificacion.Descripcion && (
                    <Typography variant="body2" component="p">
                        {notificacion.Descripcion}
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
};

const NotificacionesCarousel = (props) => {
    const { notificaciones, onSelectNotificacion, usuario } = props;
    const classes = useStyle();
    const [listaNotificaciones, setListaNotificaciones] = useState([]);

    const obtenerTiposUsuario = () => {
        const tipos = [];
        const tipoContrato = usuario?.tipo_contrato;

        // TIPO 1: Por tipo_contrato especial
        if (tipoContrato == 4) {
            tipos.push('ASIMILADOS A SALARIOS');
        } else if (tipoContrato == 5) {
            tipos.push('PERSONA FISICA CON ACTIVIDAD EMPRESARIAL');
        }

        // TIPO 2: Por tipo_puesto
        const tipoPuesto = usuario?.tipo_puesto;

        if (tipoPuesto === 'Administrativa') {
            tipos.push('ADMINISTRATIVO');
        } else if (tipoPuesto === 'Operativa') {
            tipos.push('OPERATIVO');
        } else if (tipoPuesto === 'Asesor de Ventas') {
            tipos.push('VENTAS');
        }

        // TIPO 3: Fallback por nrol si no se encontró tipo_puesto
        if (tipos.length === 0 || (tipos.length === 1 && (tipoContrato == 4 || tipoContrato == 5))) {
            const nrol = usuario?.nrol;

            if (nrol === 'Administrativa' || nrol === 'ADMINISTRATIVA' || nrol === 'Administrativo') {
                if (!tipos.includes('ADMINISTRATIVO')) {
                    tipos.push('ADMINISTRATIVO');
                }
            } else if (nrol === 'Operativa' || nrol === 'OPERATIVA' || nrol === 'Operativo') {
                if (!tipos.includes('OPERATIVO')) {
                    tipos.push('OPERATIVO');
                }
            } else if (nrol === 'Asesor de Ventas' || nrol === 'VENTAS' || nrol === 'Ventas') {
                if (!tipos.includes('VENTAS')) {
                    tipos.push('VENTAS');
                }
            }
        }

        return tipos;
    };

    const obtenerSedeUsuario = () => {
        if (usuario?.idsede) {
            return String(usuario.idsede);
        }

        if (usuario?.id_sede) {
            return String(usuario.id_sede);
        }

        if (usuario?.depto) {
            return String(usuario.depto);
        }

        return null;
    };
    const validarTipoUsuario = (notif) => {
        const tipoNotificacion = notif.TipoUsuario;

        if (!tipoNotificacion || tipoNotificacion === 'TODOS') {
            return true;
        }

        const tiposUsuarioActual = obtenerTiposUsuario();

        // Verificar si el tipo de la notificación coincide con CUALQUIERA de los tipos del usuario
        const coincide = tiposUsuarioActual.includes(tipoNotificacion);

        return coincide;
    };

    const validarSede = (notif) => {
        const sedeNotificacion = notif.idsede;

        if (!sedeNotificacion || sedeNotificacion === '7' || sedeNotificacion === 7 || sedeNotificacion === null) {
            return true;
        }

        const sedeUsuario = obtenerSedeUsuario();

        // Debe coincidir exactamente
        const coincide = String(sedeNotificacion) === String(sedeUsuario);

        return coincide;
    };

    const validarVigencia = (notif) => {
        const hoy = moment();

        if (notif.periodo === 'ANUAL') {
            const actual = new Date();
            const year = actual.getFullYear();
            const fechaInicio = notif.inicio || notif.fecha_inicio;

            if (fechaInicio) {
                let arr = fechaInicio.split('-');
                const inicio = moment(`${year}-${arr[1]}-${arr[2]}`);
                const esMismoDia = hoy.isSame(inicio, 'day');

                return esMismoDia;
            }
            return false;
        }

        // PERIODO: UNA_VEZ - Se muestra desde fecha_inicio hasta fecha_fin (24 horas)
        if (notif.periodo === 'UNA_VEZ') {
            const fechaInicio = moment(notif.fecha_inicio || notif.inicio);
            const fechaFin = notif.fecha_fin || notif.fin;

            // Verificar si hay fecha_fin definida
            if (fechaFin && fechaFin !== 'NO') {
                const fin = moment(fechaFin);
                const enRango = hoy >= fechaInicio && hoy <= fin;

                return enRango;
            } else {
                // Si no hay fecha_fin, mostrar solo ese día (comportamiento legacy)
                const esMismoDia = hoy.isSame(fechaInicio, 'day');

                return esMismoDia;
            }
        }

        // PERIODO: SEMANAL - Se muestra ese día de la semana dentro del rango
        if (notif.periodo === 'SEMANAL') {
            const fechaInicio = moment(notif.fecha_inicio || notif.inicio);
            const fechaFin = notif.fecha_fin || notif.fin;

            // Verificar que estamos en el rango de fechas
            if (fechaFin && fechaFin !== 'NO') {
                const fin = moment(fechaFin);
                if (hoy < fechaInicio || hoy > fin) {
                    return false;
                }
            } else {
                if (hoy < fechaInicio) {
                    return false;
                }
            }

            // Verificar el día de la semana
            if (notif.Dia_Semana) {
                const diasSemana = {
                    LUNES: 1,
                    MARTES: 2,
                    MIERCOLES: 3,
                    JUEVES: 4,
                    VIERNES: 5,
                    SABADO: 6,
                    DOMINGO: 0
                };

                const diaActual = hoy.day();
                const diaNotificacion = diasSemana[notif.Dia_Semana];
                const esDiaCorrecto = diaActual === diaNotificacion;

                return esDiaCorrecto;
            }
            return true;
        }

        // PERIODO: DIARIO - Se muestra todos los días dentro del rango
        const fechaInicio = moment(notif.fecha_inicio || notif.inicio);
        const fechaFin = notif.fecha_fin || notif.fin;

        if (fechaFin && fechaFin !== 'NO') {
            const fin = moment(fechaFin);
            const enRango = hoy >= fechaInicio && hoy <= fin;

            return enRango;
        } else {
            const yaInicio = hoy >= fechaInicio;

            return yaInicio;
        }
    };

    useEffect(() => {
        if (notificaciones && notificaciones.length > 0 && usuario) {
            const tiposUsuario = obtenerTiposUsuario();

            let nuevaLista = [];
            let totalSlides = 0;

            notificaciones.forEach((el) => {
                // Solo procesar notificaciones de tipo SLIDE
                if (el.slide == 1 || el.slide === '1') {
                    totalSlides++;

                    // VALIDACIÓN 1: Tipo de Usuario
                    if (!validarTipoUsuario(el)) {
                        return;
                    }

                    // VALIDACIÓN 2: Sede
                    if (!validarSede(el)) {
                        return;
                    }

                    // VALIDACIÓN 3: Vigencia
                    if (!validarVigencia(el)) {
                        return;
                    }

                    nuevaLista.push(el);
                }
            });

            setListaNotificaciones([...nuevaLista]);
        }
    }, [notificaciones, usuario]);

    // Si no hay notificaciones aprobadas, no mostrar el carrusel
    if (listaNotificaciones.length === 0) {
        return null;
    }

    return (
        <div style={{ width: '100%', padding: '20px' }}>
            <Typography variant="title" sx={{ color: '#b6a268', padding: '5px', marginBottom: '10px' }}>
                NOTIFICACIONES
            </Typography>
            <Carousel
                additionalTransfrom={0}
                arrows
                autoPlay
                autoPlaySpeed={6000}
                centerMode={false}
                className={classes.carrusel}
                containerClass="container"
                dotListClass=""
                draggable
                focusOnSelect={false}
                infinite
                itemClass=""
                keyBoardControl
                minimumTouchDrag={80}
                pauseOnHover
                renderArrowsWhenDisabled={false}
                renderButtonGroupOutside={false}
                renderDotsOutside={false}
                responsive={{
                    desktop: {
                        breakpoint: {
                            max: 3000,
                            min: 1024
                        },
                        items: 1
                    },
                    mobile: {
                        breakpoint: {
                            max: 464,
                            min: 0
                        },
                        items: 1
                    },
                    tablet: {
                        breakpoint: {
                            max: 1024,
                            min: 464
                        },
                        items: 1
                    }
                }}
                rewind={false}
                rewindWithAnimation={false}
                rtl={false}
                shouldResetAutoplay
                showDots
                sliderClass=""
                slidesToSlide={1}
                swipeable
            >
                {listaNotificaciones?.map((notificacion, index) => (
                    <TarjetaNotificacion key={`notif${index}`} notificacion={notificacion} onSelect={onSelectNotificacion} />
                ))}
            </Carousel>
        </div>
    );
};

export default NotificacionesCarousel;
